import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function TextEffectsControl({
  hasOutline = false,
  outlineColor = '#000000',
  outlineWidth = 2,
  hasShadow = false,
  shadowColor = '#000000',
  shadowOpacity = 1,
  shadowBlur = 4,
  shadowOffsetX = 2,
  shadowOffsetY = 2,
  onChangeHasOutline,
  onChangeOutlineColor,
  onChangeOutlineWidth,
  onChangeHasShadow,
  onChangeShadowColor,
  onChangeShadowOpacity,
  onChangeShadowBlur,
  onChangeShadowOffsetX,
  onChangeShadowOffsetY,
  portalRoot = null,
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open || !btnRef.current) return;

    const updatePosition = () => {
      const rect = btnRef.current.getBoundingClientRect();

      requestAnimationFrame(() => {
        const panelEl = panelRef.current;
        const panelWidth = panelEl?.offsetWidth || 288;
        const viewportWidth = window.innerWidth;
        const SCREEN_PAD = 8;

        let left = rect.left + rect.width / 2 - panelWidth / 2;

        left = Math.max(
          SCREEN_PAD,
          Math.min(left, viewportWidth - panelWidth - SCREEN_PAD),
        );

        setPanelPos({
          left,
          top: rect.top - 8,
        });
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const panel = (
    <div
      ref={panelRef}
      className="z-[200] w-[min(18rem,calc(100vw-16px))] rounded-xl border border-stone-200 bg-white p-3 shadow-xl"
      style={{
        position: 'fixed',
        left: `${panelPos.left}px`,
        top: `${panelPos.top}px`,
        transform: 'translateY(-100%)',
      }}
    >
      <div className="mb-4">
        <label className="mb-1 block text-xs font-medium text-stone-600">
          Outline
        </label>
        <select
          value={hasOutline ? 'on' : 'none'}
          onChange={(e) => onChangeHasOutline?.(e.target.value === 'on')}
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
        >
          <option value="none">None</option>
          <option value="on">On</option>
        </select>
      </div>

      {hasOutline && (
        <>
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Outline colour
            </label>
            <input
              type="color"
              value={outlineColor}
              onChange={(e) => onChangeOutlineColor?.(e.target.value)}
              className="h-10 w-full rounded border border-stone-300"
            />
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Outline width
            </label>
            <input
              type="range"
              min="1"
              max="8"
              step="1"
              value={outlineWidth}
              onChange={(e) => onChangeOutlineWidth?.(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}

      <div className="mb-4">
        <label className="mb-1 block text-xs font-medium text-stone-600">
          Shadow
        </label>
        <select
          value={hasShadow ? 'on' : 'none'}
          onChange={(e) => onChangeHasShadow?.(e.target.value === 'on')}
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
        >
          <option value="none">None</option>
          <option value="on">On</option>
        </select>
      </div>

      {hasShadow && (
        <>
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Shadow colour
            </label>
            <input
              type="color"
              value={shadowColor}
              onChange={(e) => onChangeShadowColor?.(e.target.value)}
              className="h-10 w-full rounded border border-stone-300"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Shadow opacity
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={shadowOpacity}
              onChange={(e) => onChangeShadowOpacity?.(Number(e.target.value))}
              className="w-full"
            />
            <div className="mt-1 text-xs text-stone-500">
              {Math.round(shadowOpacity * 100)}%
            </div>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Blur
            </label>
            <input
              type="range"
              min="0"
              max="20"
              step="1"
              value={shadowBlur}
              onChange={(e) => onChangeShadowBlur?.(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Offset X
            </label>
            <input
              type="range"
              min="-20"
              max="20"
              step="1"
              value={shadowOffsetX}
              onChange={(e) => onChangeShadowOffsetX?.(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">
              Offset Y
            </label>
            <input
              type="range"
              min="-20"
              max="20"
              step="1"
              value={shadowOffsetY}
              onChange={(e) => onChangeShadowOffsetY?.(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm hover:bg-stone-50"
      >
        Text Effects
      </button>

      {open && createPortal(panel, portalRoot ?? document.body)}
    </>
  );
}
